<template>
  <div class="wa-main">
    <!-- Header del módulo -->
    <div class="wa-main__header mb-4">
      <div class="d-flex align-items-center" style="gap:10px;">
        <div class="wa-main__icon">
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
            width="22"
            height="22"
            alt="WhatsApp"
          />
        </div>
        <div>
          <div style="font-weight:700;font-size:15px;color:#2d2b55;">
            WhatsApp Business
          </div>
          <div style="font-size:12px;color:#6b7280;">
            Envía mensajes y notificaciones a tus clientes por WhatsApp
          </div>
        </div>

        <!-- Modo prueba (dev) -->
        <div class="wa-dev-toggle ml-auto">
          <span class="wa-dev-toggle__label">
            <i class="el-icon-cpu"></i> Modo prueba
          </span>
          <el-switch
            v-model="devMode"
            active-color="#f59e0b"
            inactive-color="#cbd5e1"
            @change="onDevModeChange"
          />
          <el-tooltip
            placement="top"
            content="Activa la UI completa de WhatsApp aunque la línea no esté conectada. Sólo para pruebas locales — los envíos reales seguirán fallando si no hay configuración."
          >
            <i
              class="el-icon-question wa-dev-toggle__help"
              style="font-size:13px;color:#9ca3af;margin-left:4px;cursor:help;"
            ></i>
          </el-tooltip>
        </div>
      </div>
      <div v-if="devMode" class="wa-dev-banner mt-3">
        <i class="el-icon-warning-outline"></i>
        <strong>Modo prueba activo.</strong> Los drawers mostrarán la UI
        completa aunque WhatsApp no esté configurado. Desactívalo antes de subir
        a producción.
      </div>
    </div>

    <!-- Banner: plataforma no disponible -->
    <div
      v-if="platformLoaded && !platformStatus.available"
      class="wa-banner wa-banner--warning mb-4"
    >
      <i class="el-icon-warning-outline mr-2"></i>
      WhatsApp no está disponible en este momento. Contacta al soporte de
      Mydessk.
    </div>

    <!-- Tabs principales -->
    <el-tabs v-model="activeTab" type="border-card" class="wa-tabs">
      <!-- ── Tab 1: Configuración ── -->
      <el-tab-pane label="Configuración" name="config">
        <div v-if="loadingConfig" class="wa-skeleton p-2">
          <div class="wa-sk-bar wa-sk-bar--wide mb-3"></div>
          <div class="wa-sk-bar wa-sk-bar--steps mb-4"></div>
          <div class="wa-sk-bar mb-2"></div>
          <div class="wa-sk-bar wa-sk-bar--short mb-4"></div>
          <div class="wa-sk-bar wa-sk-bar--input mb-2"></div>
          <div class="wa-sk-bar wa-sk-bar--btn"></div>
        </div>

        <template v-else>
          <!-- NO conectado: mostrar wizard -->
          <WhatsappSetupWizard
            v-if="!isConnected"
            :platform-status="platformStatus"
            @connected="onConnected"
          />

          <!-- Conectado: mostrar panel de gestión -->
          <WhatsappConfigPanel
            v-else
            :config="config"
            @disconnected="onDisconnected"
            @reload="loadConfig"
          />
        </template>
      </el-tab-pane>

      <!-- ── Tab 2: Plantillas ── -->
      <el-tab-pane name="templates">
        <span slot="label">
          Plantillas
          <el-badge
            v-if="pendingTemplates > 0"
            :value="pendingTemplates"
            type="warning"
            style="margin-left:4px;"
          />
        </span>

        <div v-if="!isConnected" class="wa-tab-locked">
          <i class="el-icon-lock" style="font-size:24px;color:#9ca3af;"></i>
          <div class="mt-2" style="font-size:13px;color:#6b7280;">
            Conecta tu cuenta de WhatsApp Business para gestionar plantillas.
          </div>
        </div>
        <WhatsappTemplateList
          v-else
          ref="templateList"
          @templates-loaded="onTemplatesLoaded"
        />
      </el-tab-pane>

      <!-- ── Tab 3: Historial ── -->
      <el-tab-pane label="Historial" name="history">
        <div v-if="!isConnected" class="wa-tab-locked">
          <i class="el-icon-lock" style="font-size:24px;color:#9ca3af;"></i>
          <div class="mt-2" style="font-size:13px;color:#6b7280;">
            Conecta tu cuenta de WhatsApp Business para ver el historial.
          </div>
        </div>
        <WhatsappMessageLog v-else />
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import configService from "../../../services/configService";
import WhatsappSetupWizard from "./WhatsappSetupWizard";
import WhatsappConfigPanel from "./WhatsappConfigPanel";
import WhatsappTemplateList from "./WhatsappTemplateList";
import WhatsappMessageLog from "./WhatsappMessageLog";

export default {
  name: "WhatsappMainComponent",
  components: {
    WhatsappSetupWizard,
    WhatsappConfigPanel,
    WhatsappTemplateList,
    WhatsappMessageLog
  },
  data() {
    return {
      activeTab: "config",
      loadingConfig: true,
      platformLoaded: false,
      platformStatus: { available: false },
      config: null,
      pendingTemplates: 0,
      devMode: false
    };
  },
  computed: {
    isConnected() {
      // Muestra el panel de gestión para cualquier estado post-OAuth.
      // 'needs_verification' = OAuth ok, pendiente verificar SMS para activar en Cloud API.
      // 'active'             = totalmente listo para enviar mensajes.
      // 'disconnected'/null  = muestra el wizard de configuración.
      return (
        this.config &&
        this.config.status &&
        this.config.status !== "disconnected"
      );
    }
  },
  mounted() {
    this.devMode = localStorage.getItem("wa_dev_mode") === "1";
    this.handleOAuthReturn();
    this.loadPlatformStatus();
    this.loadConfig();
  },
  methods: {
    handleOAuthReturn() {
      const query = this.$route && this.$route.query;
      if (!query) return;
      const waStatus = query.wa_status;
      if (!waStatus) return;

      // Limpiar el query param de la URL sin recargar la página
      const cleanQuery = Object.assign({}, query);
      delete cleanQuery.wa_status;
      try {
        this.$router.replace({ query: cleanQuery });
      } catch {
        // Ignorar NavigationDuplicated
      }

      if (waStatus === "success") {
        this.$notifications.success({
          message: "¡WhatsApp conectado correctamente!"
        });
      } else if (waStatus === "error") {
        this.$notifications.error({
          message: "Ocurrió un error al conectar con Meta. Intenta de nuevo."
        });
      }
    },

    onDevModeChange(val) {
      if (val) {
        localStorage.setItem("wa_dev_mode", "1");
        this.$message({
          message:
            "Modo prueba activado. Los drawers mostrarán la UI completa.",
          type: "warning",
          duration: 3000
        });
      } else {
        localStorage.removeItem("wa_dev_mode");
        this.$message({
          message: "Modo prueba desactivado.",
          type: "success",
          duration: 2000
        });
      }
    },

    async loadPlatformStatus() {
      try {
        const res = await configService.getWhatsAppPlatformStatus();
        if (res.data && res.data.success) {
          this.platformStatus = res.data.data;
        }
      } catch {
        // No bloquear la vista si la plataforma no responde
      } finally {
        this.platformLoaded = true;
      }
    },

    async loadConfig() {
      this.loadingConfig = true;
      try {
        const res = await configService.getWhatsAppConfig();
        if (res.data.success) {
          this.config = res.data.data;
        }
      } finally {
        this.loadingConfig = false;
      }
    },

    onConnected() {
      this.loadConfig();
      // Pequeño delay para que el backend procese el token
      setTimeout(() => this.loadConfig(), 1500);
    },

    onDisconnected() {
      this.config = null;
      this.loadConfig();
    },

    onTemplatesLoaded(templates) {
      if (!templates) return;
      this.pendingTemplates = templates.filter(
        t => t.status === "draft" || t.status === "submitted"
      ).length;
    }
  }
};
</script>

<style scoped>
.wa-main__header {
  padding-bottom: 12px;
  border-bottom: 0.5px solid #e8e7e3;
}

/* ── Modo prueba (dev) ── */
.wa-dev-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}
.wa-dev-toggle__label {
  font-size: 12px;
  font-weight: 600;
  color: #4b5563;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.wa-dev-toggle__label i {
  font-size: 13px;
  color: #f59e0b;
}
.wa-dev-banner {
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12px;
  color: #92400e;
  display: flex;
  align-items: center;
  gap: 6px;
}
.wa-dev-banner i {
  font-size: 14px;
}
.wa-main__icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #f0fdf4;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.wa-tabs {
  border: 0.5px solid #e8e7e3;
  border-radius: 10px;
}
.wa-tabs :deep(.el-tabs__header) {
  background: #fafafa;
  border-radius: 10px 10px 0 0;
}
.wa-banner {
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
}
.wa-banner--warning {
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #f59e0b;
}

/* Skeleton loader */
@keyframes wa-shimmer {
  0% {
    background-position: -400px 0;
  }
  100% {
    background-position: 400px 0;
  }
}
.wa-sk-bar {
  height: 14px;
  border-radius: 6px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 800px 100%;
  animation: wa-shimmer 1.4s infinite linear;
  width: 60%;
}
.wa-sk-bar--wide {
  width: 100%;
  height: 12px;
}
.wa-sk-bar--steps {
  width: 100%;
  height: 36px;
  border-radius: 8px;
}
.wa-sk-bar--short {
  width: 80%;
}
.wa-sk-bar--input {
  width: 100%;
  height: 36px;
  border-radius: 8px;
}
.wa-sk-bar--btn {
  width: 120px;
  height: 32px;
  border-radius: 8px;
}
.wa-tab-locked {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  text-align: center;
}
</style>
